<template>
  <ValidationProvider :name="field_name" :vid="vid || field_name" :rules="rules" v-slot="{ classes, errors }">
    <div class="input-validate" :class="classes">
      <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ field_name }}<b v-if="new RegExp('(required)').test(rules)" class="text-red-500 ml-1">*</b></label>

      <!-- Safe-4 (SEC-364 A10): the country picker beside the number, as in the
           review's screenshot. It picks the example shown in the empty field,
           and when the user leaves the field a national number is written in
           that country's form (onSafe4Blur). Choosing a country on its own never
           rewrites the number. -->
      <!-- The number input comes FIRST in the markup on purpose: vee-validate
           validates the first element in its slot that has a v-model or a value,
           and with the select first it validated the country ("no"/"se") instead
           of the number, and hooked itself onto the select so it would not
           switch. flex-row-reverse draws the flag back on the left. -->
      <div class="flex flex-row-reverse" v-if="show_country">
        <input
          type="text"
          :value="shownValue"
          :disabled="disabled"
          :readonly="readonly"
          :maxlength="maxlength"
          :placeholder="placeholder || selectedCountry.example"
          @focus="focused = true"
          @input="onSafe4Input($event)"
          @blur="onSafe4Blur()"
          class="bg-gray-100 rounded-r text-sm text-gray-900 focus:outline-none border-b-4 border-gray-300 hover:border-gray-400 transition duration-500 px-3 pt-2 pb-2 disabled:opacity-50 flex-grow"
        />
        <select
          v-model="country"
          @change="onCountryPicked()"
          :disabled="disabled"
          class="bg-gray-100 text-sm text-gray-900 focus:outline-none border-b-4 border-gray-300 hover:border-gray-400 transition duration-500 px-2 pt-2 pb-2 disabled:opacity-50 rounded-l"
        >
          <option v-for="c in countries" :key="c.code" :value="c.code">{{ c.flag }} {{ c.dial }}</option>
        </select>
      </div>

      <!-- Every other alarm central: exactly the field _base_input.vue draws, and
           the number is left exactly as it is typed -->
      <input
        v-else
        type="text"
        :value="value"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxlength"
        :placeholder="placeholder"
        @input="$emit('update', $event.target.value)"
        class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-4 border-gray-300 transition duration-500 px-3 pt-2 pb-2 disabled:opacity-50"
        v-bind:class="{ 'hover:border-gray-400': new RegExp('(required)').test(rules) }"
      />
      <span>{{ errors[0] }}</span>
    </div>
  </ValidationProvider>
</template>

<script>
import siteContext from "@/utils/siteContext";

// The countries SecurCloud serves. The dial code is shown next to the flag, the
// way the review's screenshot does it.
const COUNTRIES = [
  { code: "no", flag: "🇳🇴", dial: "+47", intl: "0047", example: "97 28 38 12" },
  { code: "se", flag: "🇸🇪", dial: "+46", intl: "0046", example: "070 123 45 67" },
  { code: "nl", flag: "🇳🇱", dial: "+31", intl: "0031", example: "06 12345678" },
];

export default {
  inheritAttrs: false,
  model: {
    event: "update",
  },
  props: {
    value: { type: [String, Number], default: "" },
    field_name: { type: String, default: "" },
    rules: { type: String, default: "" },
    vid: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    maxlength: { type: [String, Number], default: undefined },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    // the country picker is a Safe-4 thing; other alarm centrals keep a plain field
    show_country: { type: Boolean, default: false },
  },
  data() {
    return {
      countries: COUNTRIES,
      // Never empty: a <select> bound to "" matches no option and renders blank,
      // which makes the picker look broken on an empty field. A number that
      // carries a country code decides; otherwise the site does.
      country: this.countryOf(this.value) || (siteContext.isNorwegianSite() ? "no" : "se"),
      // while the user is in the field it shows the number as stored, so typing
      // is never fought by the spaces the grouped view adds
      focused: false,
      // SEC-373: the national number the last leave-the-field step gave a country
      // code, and what it became - so a country picked right after can redo it
      convertedFrom: null,
      // SEC-373: true once the user typed in the field since entering it. Only
      // a typed number gets the chosen country's code on leaving the field; a
      // stored one the user only clicked through stays as it is
      typedSinceFocus: false,
    };
  },
  computed: {
    selectedCountry() {
      return COUNTRIES.find((c) => c.code === this.country) || COUNTRIES[0];
    },
    // the number as it is shown: grouped like the review's screenshot when the
    // user is not typing in it, exactly as stored when they are
    shownValue() {
      return this.focused ? this.value : this.groupForDisplay(this.value);
    },
  },
  watch: {
    // a number loaded from the device, or typed with a country code, moves the
    // picker; a number without one leaves the user's choice alone
    value(next) {
      const detected = this.countryOf(next);
      if (detected) this.country = detected;
      this.tidyStoredNumber();
    },
    // a device moved onto Safe-4: its numbers get the Safe-4 tidy-up too
    show_country(on) {
      if (on) this.tidyStoredNumber();
    },
  },
  mounted() {
    this.tidyStoredNumber();
  },
  methods: {
    /**
     * Which country a number is written for, or "" when it carries no country
     * code. Only used to move the picker - never to change the number.
     */
    countryOf(value) {
      const compact = String(value == null ? "" : value).replace(/[\s()\-./]/g, "");
      const match = COUNTRIES.find((c) => compact.startsWith(c.intl) || compact.startsWith(c.dial));
      return match ? match.code : "";
    },
    /**
     * SEC-364 A9 / SEC-373: a leading "+" is written as "00" while the user
     * types or pastes, so "+46 70 123 45 67" becomes "0046 70 123 45 67".
     * Nothing else changes while typing; the rest happens on leaving the field.
     */
    onSafe4Input(event) {
      this.convertedFrom = null;
      this.typedSinceFocus = true;
      const typed = event.target.value;
      const value = typed.replace(/^\s*\+/, "00");
      // show the "00" straight away, also when the stored number is already the same
      if (value !== typed) event.target.value = value;
      this.$emit("update", value);
    },
    /**
     * SEC-364 A9 / A10, SEC-373: when the user leaves the field, the number is
     * written the way the review asks for the chosen country. Done on leaving,
     * not while typing, so digits do not change under the user's fingers.
     *
     *  - Norway: a complete number with its country code is shortened to the
     *    national eight digits: "+47 97 28 38 12" (or "0047…", or a bare
     *    "47…") becomes "97283812". The server leaves it out too.
     *  - Sweden and the Netherlands: a national number, with its leading 0, gets
     *    the country code instead of that 0: "070 123 45 67" becomes
     *    "0046701234567" (what KUIS receives for it anyway, SEC-323), and with
     *    the Netherlands chosen "06 12345678" becomes "0031612345678" - without
     *    it KUIS would get it as a Swedish number. Only for a number typed since
     *    entering the field: clicking through a stored one changes nothing.
     *
     * A half-typed number, or one with another country's code, keeps its digits.
     *
     *  - Digits only: the spaces, dashes, brackets, dots and slashes the user
     *    typed are dropped ("97 28 38 12" becomes "97283812"). The server sends
     *    Norwegian numbers untouched apart from the country prefix (SEC-323), so
     *    they would reach KUIS with the spaces otherwise. The field still shows
     *    the number grouped (groupForDisplay).
     *
     * Anything else than digits is left as typed, for the digits-only rule to
     * point out.
     */
    onSafe4Blur() {
      this.focused = false;
      this.convertedFrom = null;
      const typed = String(this.value == null ? "" : this.value);
      let digits = this.tidySafe4Number(typed);
      const wasTyped = this.typedSinceFocus;
      this.typedSinceFocus = false;
      // Norwegian numbers have no leading 0, so this is only for Sweden and the Netherlands
      if (wasTyped && this.country !== "no" && /^0[1-9]\d{5,}$/.test(digits)) {
        const national = digits;
        digits = this.selectedCountry.intl + national.slice(1);
        this.convertedFrom = { national, result: digits };
      }
      if (digits !== typed) this.$emit("update", digits);
    },
    /**
     * SEC-364 A9 / SEC-373: the part of the tidy-up that does not depend on the
     * chosen country: "+" written as "00", digits only, and a Norwegian number
     * without its country prefix (the client, 2026-10-08: "+47 97 28 38 12",
     * "0047…" or a bare "47…" becomes "97283812"). A number with anything else
     * than digits in it is returned as it is, for the digits-only rule.
     */
    tidySafe4Number(value) {
      const typed = String(value == null ? "" : value);
      // the separators the server's KUIS mapper drops too (mappers/phone.js):
      // spaces, dashes, brackets, dots and slashes, and the bracketed Swedish
      // trunk 0 in "+46 (0)70-123 45 67", which is never dialled
      const withoutTrunkZero = typed.trim().replace(/^(\+46|0046|46)\s*\(0\)/, "$1");
      const digits = withoutTrunkZero.replace(/^\+/, "00").replace(/[\s()\-./]/g, "");
      if (!/^\d+$/.test(digits)) return typed;
      const norwegian = digits.match(/^(?:0047|47)(\d{8})$/);
      return norwegian ? norwegian[1] : digits;
    },
    /**
     * SEC-373 (R1): a number that reaches the Safe-4 field without being typed -
     * stored before these rules, imported, copied, or on a device just moved onto
     * Safe-4 - gets the same tidy-up, so an untouched "+47 97 28 38 12" does not
     * block the save with "only digits". The chosen-country step stays a typing
     * step: a stored national number is never given a country code unasked.
     */
    tidyStoredNumber() {
      if (!this.show_country || this.focused) return;
      const typed = String(this.value == null ? "" : this.value);
      const tidy = this.tidySafe4Number(typed);
      if (tidy !== typed) this.$emit("update", tidy);
    },
    /**
     * SEC-373 (R3): the number input comes before the picker, so a user who types
     * a national number and then picks its country has already left the field -
     * which gave it the previous country's code. Picking a country right after
     * redoes that step with the new country ("06 12345678", then the Netherlands:
     * "0031612345678", not "0046…"). Norway takes the number back as it was typed.
     */
    onCountryPicked() {
      const last = this.convertedFrom;
      if (!last || String(this.value == null ? "" : this.value) !== last.result) {
        this.convertedFrom = null;
        return;
      }
      const next = this.country === "no" ? last.national : this.selectedCountry.intl + last.national.slice(1);
      this.convertedFrom = { national: last.national, result: next };
      if (next !== last.result) this.$emit("update", next);
    },
    /**
     * Spaces for reading only (SEC-364 A10), the way image 2 shows a number:
     * 97 28 38 12. This never reaches the model: the number is stored as the
     * user typed it (a leading "+" as "00", and on leaving the field in the
     * chosen country's form - see onSafe4Blur), and sent to KUIS the way SEC-323 says (Rasmus,
     * 2026-10-02: "It is only a UI change. We will still send the number the
     * way KUIS accepts it."). Numbers in any other shape are shown as they are.
     */
    groupForDisplay(value) {
      const digits = String(value == null ? "" : value);
      if (!/^\d+$/.test(digits)) return digits;
      // Norwegian: eight digits, no trunk zero -> 97 28 38 12
      if (/^[2-9]\d{7}$/.test(digits)) return digits.replace(/(\d{2})(?=\d)/g, "$1 ");
      // Swedish mobile at home -> 070 123 45 67
      if (/^07\d{8}$/.test(digits)) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
      // Swedish mobile, international -> 0046 70 123 45 67
      if (/^00467\d{8}$/.test(digits)) return `0046 ${digits.slice(4, 6)} ${digits.slice(6, 9)} ${digits.slice(9, 11)} ${digits.slice(11)}`;
      return digits;
    },
  },
};
</script>
